import React, { ReactNode } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, CheckCircle } from "lucide-react";

interface GuideStep {
  title: string;
  url: string;
  number: number;
}

const guideSteps: GuideStep[] = [
  { title: "Introduction", url: "/employee-recognition-program-guide-assembly/introduction", number: 0 },
  { title: "What is Employee Recognition?", url: "/employee-recognition-program-guide-assembly/what-is-employee-recognition-assembly", number: 1 },
  { title: "Why is Employee Recognition Important?", url: "/employee-recognition-program-guide-assembly/why-is-employee-recognition-important-assembly", number: 2 },
  { title: "Types of Employee Recognition and Rewards", url: "/employee-recognition-program-guide-assembly/types-of-employee-recognition-and-rewards-assembly", number: 3 },
  { title: "Building a Successful Employee Recognition Program", url: "/employee-recognition-program-guide-assembly/building-a-successful-employee-recognition-program-assembly", number: 4 },
  { title: "Employee Recognition Impact", url: "/employee-recognition-program-guide-assembly/employee-recognition-impact-assembly", number: 5 },
  { title: "Employee Recognition Tools and Resources", url: "/employee-recognition-program-guide-assembly/employee-recognition-tools-and-resources-assembly", number: 6 },
];

interface RecognitionGuideLayoutProps {
  children: ReactNode;
  title: string;
  description: string;
  currentStep?: number;
  showNavigation?: boolean;
}

const RecognitionGuideLayout = ({ 
  children, 
  title, 
  description, 
  currentStep = 0,
  showNavigation = true
}: RecognitionGuideLayoutProps) => {
  const currentStepData = guideSteps.find(step => step.number === currentStep);
  const nextStep = guideSteps.find(step => step.number === currentStep + 1);
  const prevStep = guideSteps.find(step => step.number === currentStep - 1);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-background via-background to-primary/5">
      <Helmet>
        <title>{title} | Employee Recognition Guide | Assembly</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${title} | Employee Recognition Guide | Assembly`} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://joinassembly.com${currentStepData?.url || ""}`} />
      </Helmet>
      
      <Navbar />
      
      {/* Common Guide Title Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-b border-border/50 pt-20">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-accent/10"></div>
        </div>
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 xl:px-16 2xl:px-20 py-16">
          <div className="text-center max-w-4xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium border border-primary/20">
              <CheckCircle className="w-4 h-4" />
              Complete Recognition Guide
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              The Complete Employee Recognition Program Guide for 2026
            </h1>
            
            <p className="text-lg md:text-xl text-primary font-semibold">
              Boost Engagement, Retain Top Talent & Drive Results
            </p>
            
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Your comprehensive resource for building recognition programs that create lasting cultural change, 
              improve business outcomes, and make employees feel truly valued.
            </p>
          </div>
        </div>
      </section>
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-16 2xl:px-20 pt-8 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8 bg-card/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <h3 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wide">
                  Recognition Guide
                </h3>
                <nav className="space-y-2">
                  {guideSteps.map((step) => (
                    <a
                      key={step.number}
                      href={step.url}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-sm ${
                        step.number === currentStep
                          ? "bg-primary/10 text-primary font-medium border border-primary/20"
                          : step.number < currentStep
                          ? "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        step.number === currentStep
                          ? "bg-primary text-primary-foreground"
                          : step.number < currentStep
                          ? "bg-green-500 text-white"
                          : "bg-muted text-muted-foreground"
                      }`}>
                        {step.number < currentStep ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          step.number === 0 ? "i" : step.number
                        )}
                      </div>
                      <span className="flex-1">{step.title}</span>
                    </a>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="bg-card/80 backdrop-blur-sm shadow-lg">
              <CardContent className="p-8">
                {children}
              </CardContent>
            </Card>

            {/* Navigation Buttons */}
            {showNavigation && (
              <div className="flex justify-between items-center mt-8">
                <div>
                  {prevStep && (
                    <Button variant="outline" asChild>
                      <a href={prevStep.url} className="flex items-center gap-2">
                        <ChevronLeft className="w-4 h-4" />
                        Previous: {prevStep.title}
                      </a>
                    </Button>
                  )}
                </div>
                <div>
                  {nextStep && (
                    <Button asChild>
                      <a href={nextStep.url} className="flex items-center gap-2">
                        Next: {nextStep.title}
                        <ChevronRight className="w-4 h-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* CTA Footer */}
            <Card className="mt-8 bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/10 border-2 border-primary/20">
              <CardContent className="p-8 text-center">
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  Put recognition into action with Assembly
                </h3>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Transform your workplace culture with our comprehensive employee recognition platform. 
                  Start building meaningful connections and boosting engagement today.
                </p>
                <Button size="lg" asChild className="bg-primary hover:bg-primary/90">
                  <a href="/book-demo" className="font-medium">
                    Get Started with Assembly
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default RecognitionGuideLayout;